// Shared validation for CV/resume uploads used by the job application form.
// Keeping this in one place means the file picker and the submit-time check
// always enforce the same rules.

export const MAX_RESUME_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

export const ALLOWED_RESUME_EXTENSIONS = ["pdf", "doc", "docx"];

// Browser-reported MIME types for the allowed document formats. Used as a
// second check on top of the file extension, since an extension alone is
// trivial to spoof.
export const ALLOWED_RESUME_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

// `accept` attribute for the file <input>, derived from the same whitelist so
// the picker and the validation stay in sync.
export const RESUME_ACCEPT_ATTR = ALLOWED_RESUME_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(",");

const TYPE_ERROR = "Only .pdf, .doc, and .docx files are allowed.";
const SIZE_ERROR = "File is too large. The maximum allowed size is 2MB.";
const EMPTY_ERROR = "The selected file appears to be empty.";

/**
 * Validates a CV/resume file against the allowed extensions, MIME types and
 * the 2MB size limit. Returns an error message string when the file is
 * invalid, or null when it passes every check.
 */
export function validateResumeFile(file) {
  const ext = file?.name?.split(".")?.pop()?.toLowerCase() ?? "";
  if (!ALLOWED_RESUME_EXTENSIONS.includes(ext)) {
    return TYPE_ERROR;
  }

  // Some browsers/operating systems leave `type` empty (notably for .doc);
  // only reject when a MIME type is reported and it is not whitelisted.
  if (file.type && !ALLOWED_RESUME_MIME_TYPES.includes(file.type)) {
    return TYPE_ERROR;
  }

  if (file.size > MAX_RESUME_SIZE_BYTES) {
    return SIZE_ERROR;
  }

  if (file.size === 0) {
    return EMPTY_ERROR;
  }

  return null;
}
